import { getLocalStorage, removeLocalStorage, parseLocalStorage } from './localStorage';
import apiService from './apiService';

/**
 * Get pending referral code from localStorage
 * @returns {string|null} The pending referral code or null if not found
 */
export const getPendingReferralCode = () => {
  if (typeof window === 'undefined') return null;
  return getLocalStorage('pending_referral_code');
};

/**
 * Clear pending referral code from localStorage
 */
export const clearPendingReferralCode = () => {
  if (typeof window === 'undefined') return;
  removeLocalStorage('pending_referral_code');
};

/**
 * Check if referral code is valid (not empty, not user's own code)
 * @param {string} referralCode - The referral code to validate
 * @returns {Object} { isValid: boolean, reason?: string }
 */
export const validateReferralCode = (referralCode) => {
  // Check if referral code is empty or invalid
  if (!referralCode || typeof referralCode !== 'string' || !referralCode.trim()) {
    return { isValid: false, reason: 'Referral code is empty or invalid' };
  }

  // Get current user data
  const userData = parseLocalStorage('autobot_user');
  const userReferralCode = userData?.user?.referralCode;

  // Check if referral code matches user's own code
  if (userReferralCode && referralCode.trim() === userReferralCode.trim()) {
    return { isValid: false, reason: 'Cannot use your own referral code' };
  }

  return { isValid: true };
};

/**
 * Track referral visit via API
 * @param {string} referralCode - The referral code to track
 * @returns {Promise<Object>} API response
 */
export const trackReferralVisit = async (referralCode) => {
  try {
    // Validate referral code first
    const validation = validateReferralCode(referralCode);
    if (!validation.isValid) {
      return { success: false, reason: validation.reason };
    }

    // Call API to track referral visit
    const response = await apiService.trackReferralVisit(referralCode.trim());
    
    return response;
  } catch (error) {
    // Log error but don't throw - we don't want to block the login flow
    console.error('Error tracking referral visit:', error);
    return { success: false, error: error.message || 'Failed to track referral visit' };
  }
};

/**
 * Process pending referral code after successful authentication
 * This function should be called after login/signup
 */
export const processPendingReferral = async () => {
  try {
    const pendingCode = getPendingReferralCode();
    
    if (!pendingCode) {
      return { success: false, reason: 'No pending referral code found' };
    }

    // Track the referral visit
    return await trackReferralVisit(pendingCode);
  } catch (error) {
    console.error('Error processing pending referral:', error);
    return { success: false, error: error.message || 'Failed to process referral' };
  }
};

